/**
 * The provider named on the app URL, for a link that should skip the email step.
 *
 * This is what the welcome email uses, and it is why most first-time people never type an
 * address: the link already knows which way they go. A customer gets one pointing at MIKE
 * Cloud, a colleague one pointing at DHI's tenant, and either lands on their own identity
 * provider with nothing asked of them here.
 *
 * The name is deliberately `take`. Reading consumes: the parameter is removed from the
 * address bar in the same breath, so a reload does not repeat a decision the person did
 * not make twice, and a URL copied out of the address bar afterwards does not carry
 * somebody else's route. That makes this a side effect masquerading as a getter, which is
 * exactly how it went wrong the first time: it was called inside a `.find()` predicate, so
 * it ran once per provider, and every call after the first saw a query string it had
 * already emptied. It worked only when the URL happened to name the first provider in the
 * list. Hence a name that reads as consuming, a test that pins it, and a caller that calls
 * once.
 *
 * Nothing here trusts the value as more than a name. The caller matches it against the
 * configured providers, so an unknown or stale one falls through to the ordinary path
 * rather than mounting a gate against an authority the backend will not validate.
 */

type Loc = Pick<Location, 'search' | 'pathname' | 'hash'>;
type Hist = Pick<History, 'replaceState'>;

const pageLocation = (): Loc | null => (typeof window === 'undefined' ? null : window.location);
const pageHistory = (): Hist | null => (typeof window === 'undefined' ? null : window.history);

/**
 * Read and remove one query parameter.
 *
 * `loc` and `hist` are injectable for the same reason the storage helpers' stores are:
 * the units are worth testing and the globals are not available in the test environment.
 */
function takeParam(name: string, loc: Loc | null, hist: Hist | null): string | null {
  if (!loc) return null;
  try {
    const params = new URLSearchParams(loc.search);
    const value = params.get(name);
    if (!value) return null;
    params.delete(name);
    const query = params.toString();
    // Everything else on the URL is left alone. A deployment served under a base path
    // keeps its pathname, and an unrelated parameter or fragment survives, because this
    // knows about one parameter and should not be in the business of rewriting the rest.
    hist?.replaceState(null, '', `${loc.pathname}${query ? `?${query}` : ''}${loc.hash}`);
    return value;
  } catch {
    // A malformed query string is not worth failing a sign-in over.
    return null;
  }
}

/** Read and remove the `provider` query parameter. */
export function takeProviderFromUrl(
  loc: Loc | null = pageLocation(),
  hist: Hist | null = pageHistory(),
): string | null {
  return takeParam('provider', loc, hist);
}

/**
 * Read and remove the `auto` query parameter, which asks for an immediate sign-in.
 *
 * Carried by the link the email step follows to `/signin/<provider>`, and by a welcome
 * link that wants to skip every screen of ours. Consumed for the same reason `provider`
 * is: a page reached by Back from the identity provider must show a button, not redirect
 * again.
 */
export function takeAutoSignInFromUrl(
  loc: Loc | null = pageLocation(),
  hist: Hist | null = pageHistory(),
): boolean {
  return takeParam('auto', loc, hist) === '1';
}

/**
 * Read and remove the `login_hint` query parameter: the address typed on the email step.
 *
 * It has to cross the full page load that switching provider requires, and the URL is the
 * only thing that does. Consumed at once, so the address bar does not keep showing it and
 * a copied URL does not carry somebody's address along. Not trusted as anything more than
 * a hint: it is handed to the identity provider, which asks for credentials regardless.
 */
export function takeLoginHintFromUrl(
  loc: Loc | null = pageLocation(),
  hist: Hist | null = pageHistory(),
): string | null {
  return takeParam('login_hint', loc, hist);
}
